import React, { useId } from 'react';
import './Field.css';

/*
 * Field — label + control wrapper, the mock's `.field`.
 *   label    : the field label text
 *   hint     : optional helper text under the control
 *   error    : optional error text (replaces hint, tints control red via aria)
 *   required : shows a subtle required marker
 *   full     : span both columns inside a .formgrid (adds .field--full)
 *   htmlFor  : id of the control it labels (auto-generated + injected into the
 *              single child control if you don't pass one)
 *
 * Usage:
 *   <Field label="Gate name"><TextInput .../></Field>
 * The child control receives id + aria-describedby/aria-invalid automatically
 * when it's a single React element.
 */
export default function Field({
  label,
  hint,
  error,
  required = false,
  full = false,
  htmlFor,
  id,
  className = '',
  children,
  ...rest
}) {
  const auto = useId();
  const controlId = htmlFor || id || `fld-${auto}`;
  const describedById = (hint || error) ? `${controlId}-desc` : undefined;

  // Inject a11y wiring into a single child control.
  let control = children;
  if (React.Children.count(children) === 1 && React.isValidElement(children)) {
    control = React.cloneElement(children, {
      id: children.props.id || controlId,
      'aria-describedby': [children.props['aria-describedby'], describedById].filter(Boolean).join(' ') || undefined,
      'aria-invalid': error ? true : children.props['aria-invalid'],
      invalid: error ? true : children.props.invalid,
    });
  }

  return (
    <div className={`field ui-field${full ? ' field--full' : ''}${className ? ' ' + className : ''}`} {...rest}>
      {label != null && (
        <label htmlFor={controlId}>
          {label}{required && <span className="ui-field__req" aria-hidden="true"> *</span>}
        </label>
      )}
      {control}
      {(error || hint) && (
        <div id={describedById} className={`ui-field__msg${error ? ' ui-field__msg--error' : ''}`}>
          {error || hint}
        </div>
      )}
    </div>
  );
}
